import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Fragment, type ReactNode } from 'react';
import type { SummaryPart } from './stepSummary.js';

interface StepFooterProps {
  // Omit inside already-padded content: the footer then renders as an inset bordered card.
  gutterX?: number;
  title: string;
  detail?: string | null;
  parts?: SummaryPart[];
  children: ReactNode;
}

export function StepFooter({
  gutterX,
  title,
  detail,
  parts,
  children,
}: StepFooterProps): ReactNode {
  return (
    <Stack
      direction="row"
      data-testid="workflow-step-footer"
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        px: gutterX ?? 3,
        py: 3,
        gap: 4,
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'background.paper',
        borderColor: 'divider',
        ...(gutterX === undefined
          ? { border: '1px solid', borderRadius: 2 }
          : { borderTop: '1px solid' }),
      }}
    >
      <Stack sx={{ gap: 0.5, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{title}</Typography>
        {detail === null || detail === undefined ? null : (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{detail}</Typography>
        )}
        {parts === undefined || parts.length === 0 ? null : (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {parts.map((part, index) => (
              <Fragment key={part.text}>
                {index === 0 ? null : ' · '}
                {part.tooltip === null ? (
                  part.text
                ) : (
                  <Tooltip title={part.tooltip}>
                    <Box
                      component="span"
                      sx={{ cursor: 'help', textDecoration: 'underline dotted' }}
                    >
                      {part.text}
                    </Box>
                  </Tooltip>
                )}
              </Fragment>
            ))}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" sx={{ gap: 2, alignItems: 'center', flexShrink: 0 }}>
        {children}
      </Stack>
    </Stack>
  );
}
